import { isValidRoomCode, normalizeRoomCode } from '../../shared/protocol.js';
import { queryOne } from '../core/dom.js';

const GAME_TYPES = ['chess', 'xiangqi', 'caro', 'tycoon'];

function hostTokenKey(gameType, roomCode) {
  return `ivs-room-host-${gameType}-${roomCode}`;
}

async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

export class LiveRoomActionsController {
  constructor({
    roomsUrl,
    storage = globalThis.localStorage,
    fetchImpl = (...args) => fetch(...args),
    getCurrentRoom = () => ({}),
    getPlayerToken = async () => '',
    onActivateRoom = () => {},
    onDisconnectCurrentRoom = () => {},
    onRefresh = () => {},
    onNotice = () => {}
  } = {}) {
    this.roomsUrl = roomsUrl;
    this.storage = storage;
    this.fetchImpl = fetchImpl;
    this.getCurrentRoom = getCurrentRoom;
    this.getPlayerToken = getPlayerToken;
    this.onActivateRoom = onActivateRoom;
    this.onDisconnectCurrentRoom = onDisconnectCurrentRoom;
    this.onRefresh = onRefresh;
    this.onNotice = onNotice;
    this.pendingChange = null;
    this.mounted = false;
  }

  mount() {
    if (this.mounted) return;
    this.mounted = true;

    queryOne('#createRoomForm')?.addEventListener('submit', event => void this.createFromLobby(event));
    queryOne('#homeCreateRoomForm')?.addEventListener('submit', event => void this.createFromHome(event));
    queryOne('#changeRoomGameForm')?.addEventListener('submit', event => void this.confirmChangeGame(event));
    queryOne('#cancelChangeRoomGameBtn')?.addEventListener('click', () => this.cancelChangeGame());
    queryOne('#homeToggleCreateRoomBtn')?.addEventListener('click', () => this.toggleHomeQuickCreate());
    queryOne('#homeCloseQuickCreateBtn')?.addEventListener('click', () => this.setHomeQuickCreateVisible(false));

    for (const input of [
      queryOne('#createRoomCode'),
      queryOne('#homeCreateRoomCode'),
      queryOne('#joinRoomCode')
    ]) {
      input?.addEventListener('input', () => {
        input.value = normalizeRoomCode(input.value);
      });
    }
  }

  setHomeQuickCreateVisible(visible) {
    const section = queryOne('#homeQuickCreate');
    const toggleBtn = queryOne('#homeToggleCreateRoomBtn');
    if (!section) return;

    section.classList.toggle('hidden', !visible);
    if (toggleBtn) {
      toggleBtn.setAttribute('aria-expanded', String(visible));
      toggleBtn.classList.toggle('active', visible);
      toggleBtn.innerHTML = visible
        ? '<span class="btn-icon">✕</span> Đóng tạo phòng'
        : '<span class="btn-icon">＋</span> Tạo phòng';
    }

    if (visible) {
      section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      queryOne('#homeCreateGame')?.focus();
    }
  }

  toggleHomeQuickCreate() {
    const section = queryOne('#homeQuickCreate');
    const isHidden = !section || section.classList.contains('hidden');
    this.setHomeQuickCreateVisible(isHidden);
  }

  async createRoom({
    gameType,
    roomCode = '',
    password = '',
    allowSpectators = true,
    discoverable = true
  }) {
    const normalizedCode = normalizeRoomCode(roomCode);
    if (!GAME_TYPES.includes(gameType)) throw new Error('Loại trò chơi không hợp lệ.');
    if (normalizedCode && !isValidRoomCode(normalizedCode)) {
      throw new Error('Mã phòng cần 4–12 ký tự: chữ, số, _ hoặc -.');
    }

    let playerToken = '';
    try {
      playerToken = await this.getPlayerToken();
    } catch {
      // Account identity is optional. Room creation remains Guest-first.
    }

    const response = await this.fetchImpl(this.roomsUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        gameType,
        roomCode: normalizedCode || null,
        password,
        allowSpectators,
        discoverable,
        playerToken: playerToken || null
      })
    });
    const result = await parseJson(response);
    if (!response.ok) {
      const error = new Error(result.error || 'Không thể tạo phòng');
      error.status = response.status;
      throw error;
    }
    return result;
  }

  async closeRoom({ gameType, roomCode, hostToken }) {
    const url = new URL(`${this.roomsUrl}/${encodeURIComponent(roomCode)}`);
    url.searchParams.set('game', gameType);

    let playerToken = '';
    try { playerToken = await this.getPlayerToken(); } catch {}

    const headers = { Accept: 'application/json' };
    if (hostToken) headers['X-IVS-Host-Token'] = hostToken;
    if (playerToken) headers['X-IVS-Player-Token'] = playerToken;

    const response = await this.fetchImpl(url.toString(), {
      method: 'DELETE',
      headers
    });
    const result = await parseJson(response);
    if (!response.ok) throw new Error(result.error || 'Không thể đóng phòng cũ');
    return result;
  }

  async createFromLobby(event) {
    event.preventDefault();
    const current = this.getCurrentRoom();
    const errorEl = queryOne('#roomLobbyError');
    const button = queryOne('#createRoomBtn');
    if (errorEl) errorEl.textContent = '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Đang tạo...';
    }

    try {
      const password = queryOne('#createRoomPassword')?.value || '';
      const result = await this.createRoom({
        gameType: current.gameType,
        roomCode: queryOne('#createRoomCode')?.value || '',
        password,
        allowSpectators: queryOne('#createAllowSpectators')?.checked !== false,
        discoverable: queryOne('#createDiscoverable')?.checked === true
      });
      this.onActivateRoom({ gameType: current.gameType, result, password });
    } catch (error) {
      if (errorEl) errorEl.textContent = error.message || 'Không thể kết nối máy chủ.';
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = 'Tạo phòng';
      }
    }
  }

  async createFromHome(event) {
    event.preventDefault();
    const errorEl = queryOne('#homeCreateRoomError');
    const button = queryOne('#homeCreateRoomBtn');
    if (errorEl) errorEl.textContent = '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Đang tạo phòng...';
    }

    const gameType = queryOne('#homeCreateGame')?.value || 'chess';
    const password = queryOne('#homeCreateRoomPassword')?.value || '';

    try {
      const result = await this.createRoom({
        gameType,
        roomCode: queryOne('#homeCreateRoomCode')?.value || '',
        password,
        allowSpectators: queryOne('#homeCreateAllowSpectators')?.checked !== false,
        discoverable: queryOne('#homeCreateDiscoverable')?.checked === true
      });
      this.setHomeQuickCreateVisible(false);
      this.onActivateRoom({ gameType, result, password });
    } catch (error) {
      if (errorEl) errorEl.textContent = error.message || 'Không thể tạo phòng LIVE.';
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = 'Tạo phòng LIVE';
      }
    }
  }

  openChangeGame(room = null) {
    const current = this.getCurrentRoom();
    const roomCode = normalizeRoomCode(room?.roomCode || current.roomCode);
    const gameType = room?.gameType || current.gameType;
    const status = room?.status || current.status || 'waiting';
    const token = this.storage?.getItem(hostTokenKey(gameType, roomCode))
      || (roomCode === current.roomCode && gameType === current.gameType ? current.hostToken : '');

    const isCurrentRoom = roomCode === current.roomCode && gameType === current.gameType;
    const canUseAccountHost = isCurrentRoom && current.isHost === true;
    if (!roomCode || (!token && !canUseAccountHost)) {
      this.notice('Không tìm thấy quyền chủ phòng cho phòng này.');
      return;
    }
    if (status !== 'waiting') {
      this.notice('Chỉ có thể đổi game khi phòng đang chờ, trước khi ván bắt đầu.');
      return;
    }

    this.pendingChange = {
      roomCode,
      gameType,
      token,
      allowSpectators: room?.allowSpectators ?? current.allowSpectators ?? true,
      // WebSocket state intentionally does not expose discoverability.
      // Default private when unknown; the host can explicitly opt back in.
      discoverable: room?.discoverable ?? false,
      connected: isCurrentRoom
    };

    const select = queryOne('#changeRoomGameSelect');
    if (select) select.value = GAME_TYPES.find(type => type !== gameType) || 'chess';
    queryOne('#changeRoomGameCode').textContent = roomCode;
    queryOne('#changeRoomGamePassword').value = this.pendingChange.connected ? (current.password || '') : '';
    queryOne('#changeRoomKeepCode').checked = true;
    queryOne('#changeRoomAllowSpectators').checked = Boolean(this.pendingChange.allowSpectators);
    queryOne('#changeRoomDiscoverable').checked = Boolean(this.pendingChange.discoverable);
    queryOne('#changeRoomGameError').textContent = '';
    queryOne('#changeRoomGameDialog')?.showModal();
  }

  cancelChangeGame() {
    this.pendingChange = null;
    queryOne('#changeRoomGameDialog')?.close();
  }

  async confirmChangeGame(event) {
    event.preventDefault();
    if (!this.pendingChange) return;

    const oldRoom = this.pendingChange;
    const gameType = queryOne('#changeRoomGameSelect')?.value || '';
    const password = queryOne('#changeRoomGamePassword')?.value || '';
    const keepCode = queryOne('#changeRoomKeepCode')?.checked === true;
    const errorEl = queryOne('#changeRoomGameError');
    const button = queryOne('#confirmChangeRoomGameBtn');

    if (gameType === oldRoom.gameType) {
      if (errorEl) errorEl.textContent = 'Hãy chọn một game khác phòng hiện tại.';
      return;
    }

    if (button) {
      button.disabled = true;
      button.textContent = 'Đang đổi game...';
    }
    if (errorEl) errorEl.textContent = '';

    let created = null;
    try {
      const options = {
        gameType,
        password,
        allowSpectators: queryOne('#changeRoomAllowSpectators')?.checked !== false,
        discoverable: queryOne('#changeRoomDiscoverable')?.checked === true
      };

      try {
        created = await this.createRoom({
          ...options,
          roomCode: keepCode ? oldRoom.roomCode : ''
        });
      } catch (error) {
        if (!keepCode || error.status !== 409) throw error;
        created = await this.createRoom({ ...options, roomCode: '' });
      }

      try {
        await this.closeRoom({
          gameType: oldRoom.gameType,
          roomCode: oldRoom.roomCode,
          hostToken: oldRoom.token
        });
      } catch (closeError) {
        // Transactional rollback: replacement must not survive if the old
        // room cannot be closed with the stored host credential.
        try {
          await this.closeRoom({
            gameType,
            roomCode: created.roomCode,
            hostToken: created.hostToken
          });
        } catch {}
        throw closeError;
      }

      this.storage?.removeItem(hostTokenKey(oldRoom.gameType, oldRoom.roomCode));
      if (oldRoom.connected) this.onDisconnectCurrentRoom();

      this.pendingChange = null;
      queryOne('#changeRoomGameDialog')?.close();
      this.onActivateRoom({ gameType, result: created, password });
      this.onRefresh();
    } catch (error) {
      if (errorEl) errorEl.textContent = error.message || 'Không thể đổi game của phòng.';
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = 'Đổi game';
      }
    }
  }

  notice(message) {
    this.onNotice(message);
    const lobby = queryOne('#roomLobbyError');
    const home = queryOne('#homeRoomDirectoryNotice');
    if (lobby) lobby.textContent = message;
    if (home) home.textContent = message;
  }
}
